import { filter, find, includes, map, pick, transform } from 'lodash';
import { useCallback, useMemo } from 'react';

import { DefaultPaymentTypeCode } from 'enums';
import { PayoutRequisites, Requisites, TradeMethod } from 'types';
import { formUtils, requisitesUtils, tradeMethodsUtils } from 'utils';

import { useUserContext } from './use-user-context.hook';

export function useUserContextUtils() {
  const {
    banks,
    banksRaw,
    paymentTypes,
    fiatCurrencies,
    tradeMethods,
    allTradeMethods,
    accountNumberQrSupportedCombinations,
  } = useUserContext();

  const getTradeMethodLabel = useCallback(
    (tradeMethodId: string) =>
      tradeMethodsUtils.getTradeMethodLabel({
        tradeMethodId,
        tradeMethods: allTradeMethods,
        banks: banksRaw,
        paymentTypes,
        fiatCurrencies,
      }),
    [banksRaw, fiatCurrencies, paymentTypes, allTradeMethods],
  );

  const getTradeMethodsOptions = useCallback(
    (tradeMethodsForOptions = tradeMethods) =>
      formUtils.getOptions(
        map(tradeMethodsForOptions, (tradeMethod) => ({
          id: tradeMethod.id,
          name: getTradeMethodLabel(tradeMethod.id),
        })),
      ),
    [getTradeMethodLabel, tradeMethods],
  );

  const allTradeMethodsOptions = useCallback(
    () => getTradeMethodsOptions(allTradeMethods),
    [getTradeMethodsOptions, allTradeMethods],
  );

  const isTradeMethodSupported = useCallback(
    (requisites: Requisites | Partial<PayoutRequisites>) =>
      !!(
        find(
          tradeMethods,
          pick(requisites, ['fiatCurrencyId', 'paymentTypeId', 'bankId']),
        ) as TradeMethod
      )?.enabled,
    [tradeMethods],
  );

  const getBankName = useCallback(
    ({ id, code }: { id?: string; code?: string }) =>
      (find(banks, { id }) || find(banks, { code }))?.name || code || id,
    [banks],
  );

  const getBankId = useCallback(
    ({ code }: { code?: string }) => find(banks, { code })?.id,
    [banks],
  );

  const getPaymentTypeLabel = useCallback(
    ({ id, code }: { id?: string; code?: string }) => {
      const paymentType =
        find(paymentTypes, { id }) || find(paymentTypes, { code });
      if (!paymentType) {
        return code || id || '';
      }
      return requisitesUtils.getPaymentTypeLabel(paymentType);
    },
    [paymentTypes],
  );

  const getPaymentTypeCode = useCallback(
    ({ id }: { id?: string }) => find(paymentTypes, { id })?.code,
    [paymentTypes],
  );

  const getPaymentTypesOptions = useCallback(
    (paymentTypesForOptions = paymentTypes) =>
      requisitesUtils.getPaymentTypesOptions(paymentTypesForOptions),
    [paymentTypes],
  );

  const paymentTypesOptions = useMemo(
    () => getPaymentTypesOptions(),
    [getPaymentTypesOptions],
  );

  const crossFiatPaymentTypeIds = useMemo(
    () =>
      transform(
        paymentTypes,
        (acc: string[], type) => {
          if (
            includes(
              [DefaultPaymentTypeCode.TSBP, DefaultPaymentTypeCode.TCard2Card],
              type.code,
            )
          ) {
            acc.push(type.id);
          }
        },
        [],
      ),
    [paymentTypes],
  );

  const getArchived = useCallback(
    <T extends { archived: boolean }>(items: T[]): T[] =>
      filter(items, (item) => item.archived),
    [],
  );

  const getNotArchived = useCallback(
    <T extends { archived: boolean }>(items: T[]): T[] =>
      filter(items, (item) => !item.archived),
    [],
  );

  const isQRSupported = useCallback(
    (
      currencyId: string,
      paymentTypeId: string,
      fiatCurrencies: any[],
      paymentTypes: any[],
    ): boolean => {
      if (!currencyId || !paymentTypeId) {
        return false;
      }

      const currency = fiatCurrencies.find((f) => f.id === currencyId);
      const paymentType = paymentTypes.find((p) => p.id === paymentTypeId);

      if (!currency || !paymentType) {
        return false;
      }

      return accountNumberQrSupportedCombinations.some(
        (combination) =>
          combination.currencyCode === currency.code &&
          combination.paymentType === paymentType.code,
      );
    },
    [accountNumberQrSupportedCombinations],
  );

  return {
    getTradeMethodLabel,
    getTradeMethodsOptions,
    isTradeMethodSupported,
    allTradeMethodsOptions,
    getBankName,
    getPaymentTypeLabel,
    getPaymentTypesOptions,
    paymentTypesOptions,
    crossFiatPaymentTypeIds,
    getBankId,
    getArchived,
    getNotArchived,
    getPaymentTypeCode,
    isQRSupported,
  };
}
